import React, { useEffect, useRef, useMemo } from 'react';
import IconCollection from '../IconCollection/IconCollection';
import NamedLink from '../NamedLink/NamedLink';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Avatar from '../Avatar/Avatar';
import css from './MobileBottomNavbar.module.css';
import { FormattedMessage } from 'react-intl';
import { openStoreCartInURL } from '../../util/dateRangeHelpers';

// Deliberately no viewport/keyboard/scroll heuristics live here any more. Everything
// that used to sit between these lines - KEYBOARD_OFFSET_THRESHOLD, SCROLL_HIDE_DELAY,
// isSoftwareKeyboardVisible(), a throttle helper - existed to decide when to hide this
// bar, and every one of those decisions was made from viewport measurements that iOS
// Chrome reports differently from Safari. Three attempts at tuning them (d2b90d8,
// d79df55, a9272f0) each fixed one browser and broke another. The bar is now
// unconditionally visible, so there is nothing left to decide.

const MobileBottomNavbar = ({
    setScrolling = () => { },
    // Whether this instance owns --mobile-bottom-nav-height (see the effect below).
    // TopbarMobileMenu renders a SECOND navbar inside the mobile menu panel so the nav
    // stays visible while that full-screen overlay is open. Both instances used to
    // publish to the same custom property on <html>, and the in-menu one measures 0 while
    // the menu is closed - so it clobbered the real navbar's height with 0px, which
    // dropped the listing page's sticky booking footer to bottom:0 right on top of the
    // navbar it is supposed to sit above. Only the page-level navbar publishes.
    publishesHeight = true,
}) => {
    const navRef = useRef(null);
    const state = useSelector(state => state);
    const location = useLocation();
    const history = useHistory();

    const { user, cart, auth } = state;
    const { currentUser, currentUserNotificationCount } = user;
    const { isAuthenticated } = auth;
    const itemsCount = cart.itemsCount || 0;

    // This navbar is now a plain, always-present position:fixed bar. Nothing in JS moves
    // it, hides it, or unmounts it.
    //
    // What used to be here, and why it is gone: a scroll listener applied
    // transform: translateY(100%) on every scroll event and reverted it 150ms after
    // scrolling stopped, plus a keyboard detector that measured visualViewport geometry
    // and unmounted the navbar entirely when it decided a keyboard was open. Both were
    // reactive to viewport measurements, and both misfired on iOS Chrome specifically -
    // three separate rounds of fixes (d2b90d8, d79df55, and this one) all failed against
    // the same underlying problem: transforming a position:fixed element during scroll
    // makes iOS Chrome mis-composite it, leaving the bar stranded mid-screen, and no
    // threshold tuning makes that go away.
    //
    // Measured on real devices before this change (BrowserStack, listing page): the
    // geometry was always correct - navbar bottom exactly at innerHeight, booking footer
    // bottom exactly at navbar top, at every scroll offset - while the navbar's own class
    // flipped visible -> hidden -> visible on EVERY scroll. The layout was never wrong;
    // the transform churn was. Removing it removes the entire failure mode rather than
    // re-tuning the thresholds that guard it.
    //
    // Trade-off accepted: on Android, where the viewport resizes for the software
    // keyboard, the bar now sits above the keyboard instead of hiding. On iOS it is
    // behind the keyboard and invisible either way. That is a known, static cost; the
    // alternative was a measurement-driven hide that has broken three times.

    const isLoggedIn = useMemo(() => isAuthenticated && currentUser?.id, [
        isAuthenticated,
        currentUser?.id,
    ]);
    const isStorefrontRoute = useMemo(
        () => /\/rental-owner\/[^/]+/.test(location.pathname),
        [location.pathname]
    );

    // Keep this instance's scrolling callback quiet. Consumers (AlgoliaSearchPage's
    // floating "Map View" pill, TransactionPage) used it to hide their own fixed elements
    // while scrolling - the same transform-a-fixed-element-during-scroll pattern that
    // strands them mid-screen on iOS Chrome. Reported as a stable false once on mount so
    // those elements simply stay put; the prop stays in the signature so no caller breaks.
    useEffect(() => {
        setScrolling(false);
    }, [setScrolling]);

    // UI FIX 1 (manual QA on the mobile listing page): the sticky booking footer needs
    // to sit flush against this nav with no gap. The nav's height isn't a fixed CSS
    // number anywhere - .navbarContent is sized by its own padding + icon/label content,
    // not a height rule - so there's no single px value or token to hardcode. Measuring
    // the real rendered box and publishing it as a CSS custom property (same pattern
    // Modal.js already uses for --vh) lets any consumer read the actual current height
    // instead of guessing, and it self-corrects if this nav's content/padding ever
    // changes. ResizeObserver also fires when the @media(min-width:768px) display:none
    // rule kicks in (contentRect height 0), so consumers correctly get 0 above 768px
    // with no extra breakpoint logic needed on their end.
    // The navbar is now always rendered and never transformed, so this measurement is
    // stable for the lifetime of the page: it changes only on a real layout change
    // (orientation, the >=768px display:none breakpoint), never as a side effect of
    // scrolling or focus. That is what lets the booking footer trust it as an offset.
    //
    // navRef is on .navbarContent (the visible pill), not the outer .navbar - the outer
    // element is now a full-height (100dvh) invisible positioning box (see the .navbar
    // comment in the CSS module), and measuring it would publish the full screen height
    // instead of the bar's own height. .navbarContent's padding-bottom carries the same
    // env(safe-area-inset-bottom) the outer box used to add, so the published value is
    // unchanged from before.
    useEffect(() => {
        // Only the page-level navbar describes "space occupied at the bottom of the
        // viewport". The in-menu duplicate is inside a closed overlay and measures 0, so
        // letting it write here overwrote the real height with 0px - see publishesHeight.
        if (!publishesHeight) {
            return;
        }

        const setHeightVar = height => {
            document.documentElement.style.setProperty(
                '--mobile-bottom-nav-height',
                `${height}px`
            );
        };

        const node = navRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return;

        // Must be the BORDER-box height, to match the setHeightVar(node.offsetHeight)
        // seed below. contentRect is the content box, so it excludes .navbarContent's
        // padding-bottom: env(safe-area-inset-bottom) - which was 0 until viewport-fit=cover
        // was enabled, and is ~34px on a notched iPhone now. Mixing the two would have made
        // the seed correct and every later update ~34px short, dropping the sticky footer
        // down onto the nav.
        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry) {
                const borderBox = entry.borderBoxSize?.[0];
                setHeightVar(
                    borderBox
                        ? Math.round(borderBox.blockSize)
                        : entry.target.offsetHeight
                );
            }
        });
        observer.observe(node);
        setHeightVar(node.offsetHeight);

        return () => {
            observer.disconnect();
            // Real unmount only (navigating away from a page that renders the navbar) -
            // there is no longer any state in which this component renders null while
            // staying mounted. Nothing left to clear space for.
            setHeightVar(0);
        };
    }, [publishesHeight]);

    return (
        <div
            data-role="mobileBottomNavbar"
            className={classNames(css.navbar, 'mobileBottomNavbar')}
        >
            <div ref={navRef} className={css.navbarContent}>
                <NamedLink className={classNames(css.navItem)} name="LandingPage">
                    <div className={css.iconWrapper}>
                        <IconCollection name="homeIcon" />
                    </div>
                    <div className={css.navLink}>
                        <FormattedMessage id="MobileBottomNavbar.home" />
                    </div>
                </NamedLink>
                <NamedLink
                    className={classNames(
                        css.navItem,
                        location?.pathname.includes('/listing') && css.active
                    )}
                    name="NewListingPage"
                    to={{ search: '?mobilemenu=open' }}
                >
                    <div className={css.iconWrapper}>
                        <IconCollection name="createListing" />
                    </div>
                    <div className={css.navLink}>
                        <FormattedMessage id="MobileBottomNavbar.addListing" />
                    </div>
                </NamedLink>
                {isStorefrontRoute ? (
                    <button
                        type="button"
                        className={classNames(css.navItem, css.navButtonReset, css.cartNavItem)}
                        onClick={() => openStoreCartInURL(history, location)}
                    >
                        <div className={classNames(css.iconWrapper, css.messageIcon)}>
                            <IconCollection name="cart" />
                            <span data-cart-link="true" />
                            {itemsCount > 0 && (
                                <span className={`${css.notificationBadge} ${css.cartNotificationBadge}`}>{itemsCount}</span>
                            )}
                        </div>
                        <div className={css.navLink}>
                            <FormattedMessage id="MobileBottomNavbar.cart" />
                        </div>
                    </button>
                ) : (
                    <NamedLink
                        className={classNames(
                            css.navItem,
                            css.cartNavItem,
                            location?.pathname.includes('/cart') && css.active
                        )}
                        name="CartPage"
                    >
                        <div className={classNames(css.iconWrapper, css.messageIcon)}>
                            <IconCollection name="cart" />
                            <span data-cart-link="true" />
                            {itemsCount > 0 && (
                                <span className={`${css.notificationBadge} ${css.cartNotificationBadge}`}>{itemsCount}</span>
                            )}
                        </div>
                        <div className={css.navLink}>
                            <FormattedMessage id="MobileBottomNavbar.cart" />
                        </div>
                    </NamedLink>
                )}
                {isLoggedIn ? (
                    <>
                        <NamedLink
                            className={classNames(
                                css.navItem,
                                location?.pathname.includes('/inbox') && css.active
                            )}
                            name="InboxBasePage"
                        >
                            <div className={classNames(css.iconWrapper, css.messageIcon)}>
                                <IconCollection name="myBooking" />
                                {currentUserNotificationCount > 0 && (
                                    <span className={css.notificationBadge}>
                                        {currentUserNotificationCount}
                                    </span>
                                )}
                            </div>
                            <div className={css.navLink}>
                                <FormattedMessage id="MobileBottomNavbar.inbox" />
                            </div>
                        </NamedLink>
                    </>
                ) : (
                    <NamedLink
                        className={classNames(
                            css.navItem,
                            location?.pathname.includes('/page/about') && css.active
                        )}
                        name="CMSPage"
                        params={{ pageId: 'about' }}
                    >
                        <div className={classNames(css.iconWrapper, css.messageIcon)}>
                            <IconCollection name="aboutUs" />
                        </div>
                        <div className={css.navLink}>
                            <FormattedMessage id="MobileBottomNavbar.aboutUs" />
                        </div>
                    </NamedLink>
                )}
                {isLoggedIn ? (
                    <div className={classNames(css.navItem, css.avatarWrapper)}>
                        <Avatar className={css.avatar} user={currentUser} />
                        <span className={css.storeName}>
                            <FormattedMessage id="MobileBottomNavbar.myStore" />
                        </span>
                    </div>
                ) : (
                    <NamedLink
                        className={classNames(
                            css.navItem,
                            location?.pathname.includes('/signup') && css.active
                        )}
                        name="SignupPage"
                        to={{ search: '?mobilemenu=open' }}
                    >
                        <div className={css.iconWrapper}>
                            <IconCollection name="JOIN_NOW" />
                        </div>
                        <div className={css.navLink}>
                            <FormattedMessage id="MobileBottomNavbar.join" />
                        </div>
                    </NamedLink>
                )}
            </div>
        </div>
    );
};

export default MobileBottomNavbar;
